import { useEffect } from 'react'

import { useThemeContext } from '@space-lib/hooks'
import useTranslation from '@space-lib/hooks/useTranslation'
import BaseLayout from '@space-shell/common/components/baseLayout'
import LoaderFull from '@space-shell/common/components/loaderFull'
import { AuthStatus, useAuthContext } from '@space-shell/common/contexts/auth'
import useCustomDomainRedirect from '@space-shell/common/hooks/useCustomDomainRedirect'

import { useGoogleAnalyticsPageView } from '@space-shell/common/hooks/useGoogleAnalyticsPageView'
import useRequest from '@space-shell/common/hooks/useRequest'
import { AuthService } from '@space-shell/common/services/auth'
import { MembershipEndpoints, MembershipService } from '@space-shell/common/services/membership'
import { UserEndpoints, UserService } from '@space-shell/common/services/user'
import { CookieUtils } from '@space-shell/common/utils/cookie'
import { LanguageUtils } from '@space-shell/common/utils/language'
import { IS_CUSTOM_DOMAIN, IS_HOTMART_DOMAIN } from '@space-shell/constant/domain'
import {
  useAppStore,
  useAuthStore,
  useMembershipStore,
  useUserInfoStore
} from '@space-shell/core/store'
import { lazyRemote } from '@space-shell/lib/runtime'
import GamificationProvider from '@space-shell/modules/gamification/components/gamificationProvider'
import { IosPwaHeadTags } from '@space-shell/modules/pwa'
const UserProfileProvider = lazyRemote('community/userProfileContext', 'UserProfileProvider')
import { useParams, Outlet, useNavigate, useLocation, useSearchParams } from 'react-router-dom'

import { MembershipError } from './membershipError'

const LANG_COOKIE = 'hmLangCookie'

type RouteParams = {
  membership: string
  lang: string
}

const RouteGuard = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { token, ucode } = useAuthStore()
  const { membership, lang } = useParams<RouteParams>()
  const { authStatus, setAuthStatus } = useAuthContext()
  const { i18n } = useTranslation()
  const { theme } = useThemeContext()
  const [searchParams] = useSearchParams()
  const { setFirstAccessComplete, firstAccessComplete } = useAppStore()
  const redirectToCustomDomain = useCustomDomainRedirect()

  const isAuthenticating = authStatus === AuthStatus.AUTHENTICATING
  const isAuthenticated = authStatus === AuthStatus.AUTHENTICATED
  const isRenewing = authStatus === AuthStatus.RENEWING

  const membershipSlug = String(membership)

  const defineSlugOrDomain = () => {
    if (IS_HOTMART_DOMAIN) {
      return membershipSlug
    }

    return window.location.hostname
  }

  const slugOrDomain = defineSlugOrDomain()

  const {
    error: membershipInfoError,
    isLoading: isLoadingMembershipInfo,
    invalidate: invalidateMembershipInfo,
    isError: isMembershipDataError,
    data: membershipData
  } = useRequest(
    [MembershipEndpoints.info(slugOrDomain)],
    () => MembershipService.getInfo(slugOrDomain),
    {
      enabled: Boolean(slugOrDomain),
      cacheTime: 0,
      onSuccess: (membershipData) => {
        useMembershipStore.setState({ membershipData, membershipSlug: membershipData.slug })
        redirectToCustomDomain(membershipData)
      }
    }
  )

  useRequest([UserEndpoints.info()], () => UserService.getInfo(membershipData?.slug), {
    enabled: Boolean(membershipData) && isAuthenticated,
    onSuccess: (userInfo) => {
      useUserInfoStore.setState({ ...userInfo })
    }
  })

  useGoogleAnalyticsPageView()

  useEffect(() => {
    const langCookie = CookieUtils.getCookieByKey(document.cookie, LANG_COOKIE)

    if (!langCookie) {
      CookieUtils.setCookie(LANG_COOKIE, String(lang))
    }

    const normalizedLocale = LanguageUtils.formatLocale(langCookie ?? String(lang))
    const normalizedPath = location.pathname.replace(String(lang), normalizedLocale.toLowerCase())

    LanguageUtils.setLanguage(normalizedLocale, i18n)
    navigate({ pathname: normalizedPath, search: location.search }, { replace: true })
  }, [lang, i18n, location.pathname, location.search, navigate])

  useEffect(() => {
    if (IS_CUSTOM_DOMAIN) {
      if (firstAccessComplete) {
        return
      }

      const signInSilentCallbackParam = searchParams.get('signinSilentCallback')

      if (signInSilentCallbackParam === 'login_required') {
        setFirstAccessComplete(true)
        setAuthStatus?.(AuthStatus.UNAUTHENTICATED)
      }

      AuthService.start({ signInRedirect: false })
    }
  }, [searchParams, setFirstAccessComplete, firstAccessComplete, setAuthStatus])

  const shouldWaitForAuthentication = (IS_CUSTOM_DOMAIN && isAuthenticating) || isRenewing

  const shouldRenderLoading = shouldWaitForAuthentication || isLoadingMembershipInfo

  const canHandlePwaInit = membershipData?.settings

  if (shouldRenderLoading) {
    return <LoaderFull />
  }

  if (isMembershipDataError) {
    return (
      <MembershipError
        error={membershipInfoError}
        invalidateMembershipInfoRequest={invalidateMembershipInfo}
      />
    )
  }

  return (
    <>
      {canHandlePwaInit && <IosPwaHeadTags data={membershipData} />}
      <UserProfileProvider token={token} loggedUserUcode={ucode} locale={lang} theme={theme}>
        <GamificationProvider>
          <BaseLayout>
            <Outlet />
          </BaseLayout>
        </GamificationProvider>
      </UserProfileProvider>
    </>
  )
}

export default RouteGuard
